import { Card, CardContent } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface StatTileWidgetProps {
  config: {
    title?: string;
    metricKey?: string;
    format?: 'number' | 'currency' | 'percentage';
    showTrend?: boolean;
    colorScheme?: 'primary' | 'success' | 'warning' | 'destructive';
  };
  data?: {
    value?: number;
    previousValue?: number;
    label?: string;
  };
}

export function StatTileWidget({ config, data }: StatTileWidgetProps) {
  const value = data?.value ?? 0;
  const previousValue = data?.previousValue;
  const title = config.title || 'Statistic';
  
  const formatValue = (val: number) => {
    switch (config.format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return val.toLocaleString();
    }
  };

  const getTrend = () => {
    if (!config.showTrend || previousValue === undefined) return null;
    const change = value - previousValue;
    const percentChange = previousValue ? ((change / previousValue) * 100).toFixed(1) : 0;
    
    if (change > 0) {
      return { icon: TrendingUp, value: `+${percentChange}%`, positive: true };
    } else if (change < 0) {
      return { icon: TrendingDown, value: `${percentChange}%`, positive: false };
    }
    return { icon: Minus, value: '0%', positive: null };
  };

  const trend = getTrend();
  
  const colorClasses = {
    primary: 'text-primary',
    success: 'text-green-600',
    warning: 'text-yellow-600',
    destructive: 'text-destructive',
  };

  return (
    <Card className="h-full">
      <CardContent className="p-4 flex flex-col justify-between h-full">
        <p className="text-sm text-muted-foreground font-medium">{title}</p>
        <div className="mt-2">
          <p className={cn('text-3xl font-bold', colorClasses[config.colorScheme || 'primary'])}>
            {formatValue(value)}
          </p>
          {trend && (
            <div className={cn(
              'flex items-center gap-1 mt-1 text-sm',
              trend.positive === true && 'text-green-600',
              trend.positive === false && 'text-destructive',
              trend.positive === null && 'text-muted-foreground'
            )}>
              <trend.icon className="h-4 w-4" />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
